import React, { useEffect, useRef, useState } from 'react';
import PageLoader from '../../../shared/PageLoader';
import { Box, IconButton, Modal, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../../redux/hook';
import editIcon from "./../../../../assets/img/icon/editIcon.svg";
import {
  getLocations,
} from '../../../../redux/features/performerClient.slice';
import { useGetUserCompanies } from '../../../../backend/query';
import { FULL_USER_COMPANIES_PAYLOAD } from '../../../../backend/license';
import {
  getHolidaysList,
  addHoliday,
  editHoliday,
  deleteHoliday,
  uploadHoliday,
  resetAddHolidayDetails,
  resetEditHolidayDetails,
  resetDeleteHolidayDetails,
  resetUploadHolidayDetails,
} from '../../../../redux/features/holidayList.slice';
import { toast } from 'react-toastify';
import { IoMdClose } from 'react-icons/io';
import { ERROR_MESSAGES } from '../../../../utils/constants';
import { downloadFileContent, preventDefault } from '../../../../utils/common';
import { useExportHolidayList } from '../../../../backend/exports';
import Icon from '../../../common/Icon';
import Select from 'react-select';
import PerformerDropdownIndicator from '../shared/PerformerDropdownIndicator';
import PerformerTable from '../shared/PerformerTable';
import { DEFAULT_OPTIONS_PAYLOAD } from '../../../common/Table';
import { hasUserAccess } from '../../../../backend/auth';
import { USER_PRIVILEGES } from '../../UserManagement/Roles/RoleConfiguration';
import { InputModuleService } from '../../../../backend/inputModule';
import '../shared/performer-client.css';

const INCLUDES_ASSOCIATIONS = ['company', 'associate_company', 'state', 'location'];

const INCLUDE_ASSOCIATION_COLUMNS = {
  company: { only: ['id', 'name'] },
  associate_company: { only: ['id', 'name'] },
  state: { only: ['id', 'name', 'code'] },
  location: { only: ['id', 'name', 'code'] },
};

const MONTH_LIST = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const DAYS_LIST = Array.from({ length: 31 }, (_, i) => i + 1);

function generateYearsList() {
  const years = [];
  for (let y = new Date().getFullYear(); y >= 1950; y--) {
    years.push(y);
  }
  return years;
}

const YEAR_LIST = generateYearsList();

const HolidayListPerformer = () => {
  const dispatch = useAppDispatch();

  // Redux selectors
  const holidayListDetails = useAppSelector((state) => state.holidayList.holidayListDetails);
  const deleteHolidayDetails = useAppSelector((state) => state.holidayList.deleteHolidayDetails);
  const addHolidayDetails = useAppSelector((state) => state.holidayList.addHolidayDetails);
  const uploadHolidayDetails = useAppSelector((state) => state.holidayList.uploadHolidayDetails);
  const editHolidayDetails = useAppSelector((state) => state.holidayList.editHolidayDetails);
  const { userCompanies, isFetching: isFetchingCompanies } = useGetUserCompanies(FULL_USER_COMPANIES_PAYLOAD, true);
  const locationsDetails = useAppSelector((state) => state.performerClient.locationsDetails);

  // Filter state
  const [filterCompany, setFilterCompany] = useState('');
  const [filterAssocCompany, setFilterAssocCompany] = useState('');
  const [filterState, setFilterState] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Pagination / sort
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [activeSort, setActiveSort] = useState('name');
  const [sortType, setSortType] = useState<'asc' | 'desc'>('asc');

  // Modal state
  const [openModal, setOpenModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [openBulkDeleteModal, setOpenBulkDeleteModal] = useState(false);

  // Bulk selection state
  const [selectedHolidays, setSelectedHolidays] = useState<any[]>([]);
  const [bulkDeleteIds, setBulkDeleteIds] = useState<any[]>([]);
  const [deleteAction, setDeleteAction] = useState('');
  const [deleteCount, setDeleteCount] = useState(0);
  const [deleting, setDeleting] = useState(false);

  // Add/Edit form fields
  const [formName, setFormName] = useState('');
  const [formCompany, setFormCompany] = useState('');
  const [formAssocCompany, setFormAssocCompany] = useState('');
  const [formState, setFormState] = useState('');
  const [formLocation, setFormLocation] = useState('');
  const [formYear, setFormYear] = useState('');
  const [formMonth, setFormMonth] = useState('');
  const [formDay, setFormDay] = useState('');
  const [formOptionalHoliday, setFormOptionalHoliday] = useState(true);
  const [formRestricted, setFormRestricted] = useState(true);

  // Form-level dropdown data
  const [formCompanies, setFormCompanies] = useState<any[]>([]);
  const [formAssocCompanies, setFormAssocCompanies] = useState<any[]>([]);
  const [formStates, setFormStates] = useState<any[]>([]);
  const [formLocations, setFormLocations] = useState<any[]>([]);
  const [formAllLocationMappings, setFormAllLocationMappings] = useState<any[]>([]);
  const [formDays, setFormDays] = useState<number[]>(DAYS_LIST);

  // Current holiday for edit/delete
  const [currentHoliday, setCurrentHoliday] = useState<any>({});
  const isEditLoading = useRef(false);

  // Upload
  const [uploadData, setUploadData] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const MAX_FILE_SIZE_MB = 25;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
  const ALLOWED_EXTENSIONS = ['.xls', '.xlsx'];

  const validateAndSetFile = (file: File | null | undefined) => {
    if (!file) return;
    const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      toast.error('Invalid file format. Allowed formats: XLS, XLSX');
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`File size exceeds ${MAX_FILE_SIZE_MB}MB limit`);
      return;
    }
    setUploadData([file]);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const holidays = holidayListDetails.data.items || [];
  const holidayCount = (holidayListDetails.data.meta || {}).total_count || 0;

  const companies = userCompanies || [];
  const assocCompanies = filterCompany
    ? ((companies.find((c: any) => c.id === filterCompany) || {}).associate_companies || [])
    : [];
  const filterAssocCompanyLocations = filterAssocCompany
    ? ((assocCompanies.find((c: any) => c.id === filterAssocCompany) || {}).locations || [])
    : [];

  const { exportRecords, exporting } = useExportHolidayList(
    (_response: any) => {                                   
      toast.success('Export has been sent to your registered email.');                                                                                                                                      
    },
    () => { toast.error(ERROR_MESSAGES.DEFAULT); },                                                                                                                                                         
  );

  const loading =
    exporting ||
    editHolidayDetails.status === 'loading' ||
    uploadHolidayDetails.status === 'loading' ||
    addHolidayDetails.status === 'loading' ||
    deleteHolidayDetails.status === 'loading' ||
    holidayListDetails.status === 'loading';

  // ─── Helpers ────────────────────────────────────────────────────────────────

  function buildFilters(overrideFilters: any = {}) {
    const filters: any = {};
    if (filterCompany) filters.company_id_eq = filterCompany;
    if (filterAssocCompany) filters.associate_company_id_eq = filterAssocCompany;
    if (filterState) filters.state_id_eq = filterState;
    if (filterLocation) filters.location_id_eq = filterLocation.split('^')[0];
    if (filterYear) filters.year_eq = filterYear;
    if (filterMonth) filters.month_eq = String(MONTH_LIST.findIndex((m) => m === filterMonth) + 1);
    if (searchInput) filters.name_cont = searchInput;
    return { ...filters, ...overrideFilters };
  }

  function buildPayload(overrides: any = {}) {
    let filters;
    if (overrides.rawFilters !== undefined) {
      // rawFilters bypasses buildFilters entirely — strip undefined values
      filters = Object.fromEntries(Object.entries(overrides.rawFilters).filter(([, v]) => v !== undefined && v !== ''));
    } else {
      filters = buildFilters(overrides.filters);
    }
    return {
      filters,
      includes: INCLUDES_ASSOCIATIONS,
      include: INCLUDE_ASSOCIATION_COLUMNS,
      per: overrides.per !== undefined ? overrides.per : rowsPerPage,
      page: overrides.page !== undefined ? overrides.page : page + 1,
      sorts: overrides.sorts || [`${activeSort} ${sortType}`],
      includeCentral: true,
    };
  }

  function fetchHolidays(overrides: any = {}) {
    dispatch(getHolidaysList(buildPayload(overrides)));
  }

  function resetForm() {
    setFormName('');
    setFormCompany('');
    setFormAssocCompany('');
    setFormState('');
    setFormLocation('');
    setFormYear('');
    setFormMonth('');
    setFormDay('');
    setFormOptionalHoliday(true);
    setFormRestricted(true);
    setFormAssocCompanies([]);
    setFormStates([]);
    setFormLocations([]);
    setFormAllLocationMappings([]);
    setFormDays(DAYS_LIST);
  }

  function formatDate(holiday: any) {
    if (!holiday.year || !holiday.month || !holiday.day) return '-';
    return `${String(holiday.day).padStart(2, '0')}-${String(holiday.month).padStart(2, '0')}-${holiday.year}`;
  }

  // ─── Initial load ────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchHolidays({ filters: {}, page: 1, sorts: ['name asc'] });
  }, []);

  useEffect(() => {
    if (userCompanies && userCompanies.length > 0) {
      setFormCompanies(userCompanies);
    }
  }, [userCompanies]);

  // ─── Filter cascade effects ──────────────────────────────────────────────────

  useEffect(() => {
    if (filterCompany) {
      setFilterAssocCompany('');
      setFilterState('');
      setFilterLocation('');
      setFilterYear('');
      setFilterMonth('');
      setPage(0);
      fetchHolidays({ rawFilters: { company_id_eq: filterCompany }, page: 1 });
    }
  }, [filterCompany]);

  useEffect(() => {
    if (filterAssocCompany) {
      setFilterState('');
      setFilterLocation('');
      setFilterYear('');
      setFilterMonth('');
      setPage(0);
      fetchHolidays({ rawFilters: { company_id_eq: filterCompany, associate_company_id_eq: filterAssocCompany }, page: 1 });
    }
  }, [filterAssocCompany]);

  useEffect(() => {
    if (filterState) {
      setFilterLocation('');
      setFilterYear('');
      setFilterMonth('');
      setPage(0);
      fetchHolidays({ rawFilters: { company_id_eq: filterCompany, associate_company_id_eq: filterAssocCompany, state_id_eq: filterState }, page: 1 });
    }
  }, [filterState]);

  useEffect(() => {
    if (filterLocation) {
      setFilterYear('');
      setFilterMonth('');
      setPage(0);
      fetchHolidays({
        rawFilters: {
          company_id_eq: filterCompany,
          associate_company_id_eq: filterAssocCompany,
          state_id_eq: filterState || undefined,
          location_id_eq: filterLocation.split('^')[0],
        },
        page: 1,
      });
    }
  }, [filterLocation]);

  useEffect(() => {
    if (filterYear) {
      setFilterMonth('');
      setPage(0);
      fetchHolidays({
        rawFilters: {
          company_id_eq: filterCompany,
          associate_company_id_eq: filterAssocCompany,
          state_id_eq: filterState || undefined,
          location_id_eq: filterLocation ? filterLocation.split('^')[0] : undefined,
          year_eq: filterYear,
        },
        page: 1,
      });
    }
  }, [filterYear]);

  useEffect(() => {
    if (filterMonth) {
      setPage(0);
      fetchHolidays({ page: 1 });
    }
  }, [filterMonth]);

  // ─── Form cascade effects ────────────────────────────────────────────────────

  useEffect(() => {
    if (formCompany) {
      const selectedCompany = companies.find((c: any) => c.id === formCompany);
      setFormAssocCompanies((selectedCompany && selectedCompany.associate_companies) || []);
      if (!isEditLoading.current) {
        setFormAssocCompany('');
        setFormState('');
        setFormLocation('');
      }
    }
  }, [formCompany]);

  useEffect(() => {
    if (formAssocCompany) {
      if (!isEditLoading.current) {
        dispatch(getLocations({
          ...DEFAULT_OPTIONS_PAYLOAD,
          filters: { company_id_eq: formAssocCompany },
          includes: { location: { city: ['state'] } },
          include: {
            location: {
              only: ['id', 'name', 'code'],
              include: {
                city: {
                  only: ['id', 'name', 'code'],
                  include: { state: { only: ['id', 'name', 'code'] } },
                },
              },
            },
          },
        })).then((res: any) => {
          const items = ((((res || {}).payload || {}).data || {}).items) || [];
          setFormAllLocationMappings(items);
          const stateMap = new Map();
          items.forEach((item: any) => {
            const state = item.location && item.location.city && item.location.city.state;
            if (state && state.id && !stateMap.has(state.id)) {
              stateMap.set(state.id, state);
            }
          });
          setFormStates(Array.from(stateMap.values()));
          setFormLocations([]);
        });
        setFormState('');
        setFormLocation('');
      }
    }
  }, [formAssocCompany]);

  useEffect(() => {
    if (formState) {
      // Filter locations from already-fetched mappings by selected state
      const filtered = formAllLocationMappings.filter((item: any) => {
        const state = item.location && item.location.city && item.location.city.state;
        return state && state.id === formState;
      });
      setFormLocations(filtered);
      if (!isEditLoading.current) {
        setFormLocation('');
      }
    }
  }, [formState]);

  useEffect(() => {
    if (formMonth) {
      const updatedDays = [...DAYS_LIST];
      if (['April', 'June', 'September', 'November'].includes(formMonth)) {
        updatedDays.splice(30, 1);
      } else if (formMonth === 'February') {
        const y = parseInt(formYear);
        if (y && ((y % 4 === 0 && y % 100 !== 0) || y % 400 === 0)) {
          updatedDays.splice(29, 2);
        } else {
          updatedDays.splice(28, 3);
        }
      }
      setFormDays(updatedDays);
      if (formDay && !updatedDays.includes(parseInt(formDay))) {
        setFormDay('');
      }
    }
  }, [formMonth, formYear]);

  // ─── Action result effects ───────────────────────────────────────────────────

  useEffect(() => {
    if (deleteHolidayDetails.status === 'succeeded') {
      if (deleteAction === 'bulk') {
        if (bulkDeleteIds.length > 0) {
          const remaining = [...bulkDeleteIds];
          const nextId = remaining.shift();
          setBulkDeleteIds(remaining);
          setDeleteCount((c) => c + 1);
          dispatch(resetDeleteHolidayDetails());
          dispatch(deleteHoliday(nextId));
        } else {
          toast.success('Holidays deleted successfully.');
          dispatch(resetDeleteHolidayDetails());
          setOpenBulkDeleteModal(false);
          setDeleting(false);
          setDeleteAction('');
          setDeleteCount(0);
          setSelectedHolidays([]);
          fetchHolidays();
        }
      } else {
        toast.success('Holiday deleted successfully.');
        dispatch(resetDeleteHolidayDetails());
        setOpenDeleteModal(false);
        fetchHolidays();
      }
    } else if (deleteHolidayDetails.status === 'failed') {
      toast.error(ERROR_MESSAGES.DEFAULT);
      dispatch(resetDeleteHolidayDetails());
      setDeleting(false);
      setDeleteAction('');
    }
  }, [deleteHolidayDetails.status, bulkDeleteIds]);

  useEffect(() => {
    if (addHolidayDetails.status === 'succeeded') {
      if (addHolidayDetails.data.key === 'FAILURE') {
        toast.error(addHolidayDetails.data.description || ERROR_MESSAGES.DEFAULT);
      } else {
        toast.success('Holiday added successfully.');
        resetForm();
        setOpenModal(false);
        fetchHolidays();
      }
      dispatch(resetAddHolidayDetails());
    } else if (addHolidayDetails.status === 'failed') {
      toast.error(ERROR_MESSAGES.DEFAULT);
      dispatch(resetAddHolidayDetails());
    }
  }, [addHolidayDetails.status]);

  useEffect(() => {
    if (editHolidayDetails.status === 'succeeded') {
      toast.success('Holiday updated successfully.');
      resetForm();
      setOpenModal(false);
      fetchHolidays();
      dispatch(resetEditHolidayDetails());
    } else if (editHolidayDetails.status === 'failed') {
      toast.error(ERROR_MESSAGES.DEFAULT);
      dispatch(resetEditHolidayDetails());
    }
  }, [editHolidayDetails.status]);

  useEffect(() => {
    if (uploadHolidayDetails.status === 'succeeded') {
      toast.success('Data Upload is in progress, you will receive an email once its complete');
      dispatch(resetUploadHolidayDetails());
      setOpenUploadModal(false);
      setUploadData(null);
      fetchHolidays();
    } else if (uploadHolidayDetails.status === 'failed') {
      toast.error(uploadHolidayDetails.error || ERROR_MESSAGES.DEFAULT);
      dispatch(resetUploadHolidayDetails());
    }
  }, [uploadHolidayDetails.status]);

  // ─── Handlers ────────────────────────────────────────────────────────────────

  const onClickAdd = () => {
    resetForm();
    setModalType('Add');
    setOpenModal(true);
  };

  const onclickEdit = (holiday: any) => {
    isEditLoading.current = true;

    const companyId = (holiday.company && holiday.company.id) || '';
    const assocCompanyId = (holiday.associate_company && holiday.associate_company.id) || '';
    const stateId = holiday.state_id || '';
    const locationId = holiday.location_id || '';

    // Populate associate companies from the selected company
    const selectedCompany = companies.find((c: any) => c.id === companyId);
    setFormAssocCompanies((selectedCompany && selectedCompany.associate_companies) || []);

    // Fetch locations for the associate company, then populate states & locations
    dispatch(getLocations({
      ...DEFAULT_OPTIONS_PAYLOAD,
      filters: { company_id_eq: assocCompanyId },
      includes: { location: { city: ['state'] } },
      include: {
        location: {
          only: ['id', 'name', 'code'],
          include: {
            city: {
              only: ['id', 'name', 'code'],
              include: { state: { only: ['id', 'name', 'code'] } },
            },
          },
        },
      },
    })).then((res: any) => {
      const items = ((((res || {}).payload || {}).data || {}).items) || [];
      setFormAllLocationMappings(items);

      // Extract unique states
      const stateMap = new Map();
      items.forEach((item: any) => {
        const state = item.location && item.location.city && item.location.city.state;
        if (state && state.id && !stateMap.has(state.id)) {
          stateMap.set(state.id, state);
        }
      });
      setFormStates(Array.from(stateMap.values()));

      // Filter locations by selected state
      const filtered = items.filter((item: any) => {
        const state = item.location && item.location.city && item.location.city.state;
        return state && state.id === stateId;
      });
      setFormLocations(filtered);

      isEditLoading.current = false;
    });

    setFormName(holiday.name || '');
    setFormCompany(companyId);
    setFormAssocCompany(assocCompanyId);
    setFormState(stateId);
    setFormLocation(locationId);
    setFormYear(String(holiday.year || ''));
    setFormMonth(MONTH_LIST[(holiday.month || 1) - 1] || '');
    setFormDay(String(holiday.day || ''));
    setFormOptionalHoliday(holiday.optional_holiday !== false);
    setFormRestricted(holiday.restricted !== false);
    setCurrentHoliday(holiday);
    setModalType('Edit');
    setOpenModal(true);
  };

  const onclickDelete = (holiday: any) => {
    setCurrentHoliday(holiday);
    setOpenDeleteModal(true);
  };

  const onclickView = (holiday: any) => {
    setFormName(holiday.name || '');
    setFormCompany((holiday.company && holiday.company.id) || '');
    setFormAssocCompany((holiday.associate_company && holiday.associate_company.id) || '');
    setFormState(holiday.state_id || '');
    setFormLocation(holiday.location_id || '');
    setFormYear(String(holiday.year || ''));
    setFormMonth(MONTH_LIST[(holiday.month || 1) - 1] || '');
    setFormDay(String(holiday.day || ''));
    setFormOptionalHoliday(holiday.optional_holiday !== false);
    setFormRestricted(holiday.restricted !== false);
    setCurrentHoliday(holiday);
    setModalType('View');
    setOpenModal(true);
  };

  const onClickBulkDelete = () => {
    setOpenBulkDeleteModal(true);
    setDeleteAction('bulk');
  };

  const onClickConfirmBulkDelete = () => {
    setDeleting(true);
    const tempDeleteIds = [...selectedHolidays];
    const id = tempDeleteIds.shift();
    setBulkDeleteIds(tempDeleteIds);
    setDeleteCount(0);
    dispatch(deleteHoliday(id));
  };

  const onClickSubmitAdd = () => {
    if (!formName || !formCompany || !formAssocCompany || !formLocation || !formYear || !formMonth || !formDay) {
      return toast.error(ERROR_MESSAGES.FILL_ALL);
    }
    dispatch(addHoliday({
      name: formName,
      optional_holiday: formOptionalHoliday,
      company_id: formCompany,
      associate_company_id: formAssocCompany,
      location_id: formLocation,
      state_id: formState,
      year: formYear,
      month: String(MONTH_LIST.findIndex((m) => m === formMonth) + 1),
      day: formDay,
      restricted: formRestricted,
      remarks: '',
    }));
  };

  const onClickSubmitEdit = () => {
    if (!formName || !formCompany || !formAssocCompany || !formLocation || !formYear || !formMonth || !formDay) {
      return toast.error(ERROR_MESSAGES.FILL_ALL);
    }
    dispatch(editHoliday({
      id: currentHoliday.id,
      name: formName,
      optional_holiday: formOptionalHoliday,
      company_id: formCompany,
      associate_company_id: formAssocCompany,
      location_id: formLocation,
      state_id: formState,
      year: formYear,
      month: String(MONTH_LIST.findIndex((m) => m === formMonth) + 1),
      day: formDay,
      restricted: formRestricted,
      remarks: '',
    }));
  };

  const onClickConfirmDelete = () => {
    dispatch(deleteHoliday(currentHoliday.id));
  };

  const onClickSubmitUpload = () => {
    if (!uploadData) return;
    const formData = new FormData();
    formData.append('file', uploadData[0]);
    dispatch(uploadHoliday(formData));
  };

  const downloadSample = async (e: any) => {
    preventDefault(e);
    try {
      const inputModuleService = new InputModuleService();
      await inputModuleService.emailSampleFormat({
        s3_key: "bulkuploadtemplates/HolidaysTemplate.xlsx",
        s3_bucket: "mafoi",
        configuration_type: "Holiday",
      });
      toast.success(
        "The template will be sent on your registered email."
      );
    } catch (e) {
      toast.error("Failed to send sample format. Please try again.");
    }
  };

  const onClickExport = () => {
    const payload = buildPayload({ per: undefined, page: undefined });
    exportRecords(payload);
  };

  const handleSearch = () => {
    setPage(0);
    fetchHolidays({ page: 1 });
  };

  // Debounced search on input change
  const searchTimerRef = useRef<any>(null);
  const isFirstSearchRender = useRef(true);
  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    searchTimerRef.current = setTimeout(() => {
      setPage(0);
      fetchHolidays({ page: 1 });
    }, 500);
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, [searchInput]);

  const handleSort = (sortField: string) => {
    const newDir: 'asc' | 'desc' = activeSort === sortField && sortType === 'asc' ? 'desc' : 'asc';
    setActiveSort(sortField);
    setSortType(newDir);
    fetchHolidays({ sorts: [`${sortField} ${newDir}`] });
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
    fetchHolidays({ page: newPage + 1 });
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchHolidays({ per: newRowsPerPage, page: 1 });
  };

  // ─── Dropdown options ─────────────────────────────────────────────────────────

  const companyOptions = companies.map((c: any) => ({ value: c.id, label: c.name }));
  const assocCompanyOptions = assocCompanies.map((c: any) => ({ value: c.id, label: c.name }));

  // Extract unique states from the selected associate company's locations (from user_companies API)
  const stateOptions = (() => {
    const seen = new Set<string>();
    const opts: { value: string; label: string }[] = [];
    filterAssocCompanyLocations.forEach((loc: any) => {
      const state = loc && loc.state;
      if (state && state.id && !seen.has(state.id)) {
        seen.add(state.id);
        opts.push({ value: state.id, label: state.name });
      }
    });
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  })();

  const locationOptions = filterAssocCompanyLocations
    .filter((loc: any) => {
      if (!loc || !loc.id || !loc.state) return false;
      if (filterState && loc.state.id !== filterState) return false;
      return true;
    })
    .slice()
    .sort((a: any, b: any) => {
      const stateCmp = (a.state.name || '').localeCompare(b.state.name || '');
      if (stateCmp !== 0) return stateCmp;
      return (a.name || '').localeCompare(b.name || '');
    })
    .map((loc: any) => {
      const cityCode = (loc.city && loc.city.code) || '';
      return {
        value: `${loc.id}^${loc.state.id}`,
        label: `${loc.name} (${loc.state.code}-${cityCode}-${loc.code})`,
      };
    });

  const formCompanyOptions = formCompanies.map((c: any) => ({ value: c.id, label: c.name }));
  const formAssocCompanyOptions = formAssocCompanies.map((c: any) => ({ value: c.id, label: c.name }));
  const formStateOptions = formStates
    .map((s: any) => ({ value: s.id, label: s.name }))
    .sort((a: any, b: any) => (a.label || '').localeCompare(b.label || ''));
  const formLocationOptions = formLocations
    .map((l: any) => ({ value: l.location_id, label: (l.location && l.location.name) || l.name }))
    .sort((a: any, b: any) => (a.label || '').localeCompare(b.label || ''));

  const closeFormModal = () => {
    setOpenModal(false);
    setModalType('');
    resetForm();
  };

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <>
    <div className="performerclient-wrapper h-100">
      <div className="performer-client-page h-100 py-3">

        {/* Delete Confirmation Modal */}
        <Modal
          open={openDeleteModal}
          onClose={() => setOpenDeleteModal(false)}
        >
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 480, borderRadius: '8px', outline: 'none', backgroundColor: '#FFFFFF', fontFamily: 'Montserrat !important' }}>
            <Box sx={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Typography sx={{ fontSize: '20px', fontWeight: 600, color: '#1E1E1E', lineHeight: '28px', fontFamily: 'Montserrat !important' }}>
                Delete Holiday
              </Typography>
              <IconButton onClick={() => setOpenDeleteModal(false)} sx={{ fontSize: '20px', padding: '0px' }}>
                <IoMdClose />
              </IconButton>
            </Box>
            <hr style={{ margin: '0px 16px' }} />
            <Box sx={{ padding: '16px' }}>
              <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#1E1E1E', fontFamily: 'Montserrat !important' }}>
                Are you sure you want to delete holiday <strong>{currentHoliday.name}</strong>?
              </Typography>
            </Box>
            <hr style={{ margin: '0px 16px' }} />
            <Box sx={{ padding: '16px', display: 'flex', gap: '24px' }}>
              <button className="button-secondary w-50" onClick={() => setOpenDeleteModal(false)}>No</button>
              <button className="button-primary w-50" onClick={onClickConfirmDelete}>Yes</button>
            </Box>
          </Box>
        </Modal>

        {/* Bulk Delete Confirmation Modal */}
        <Modal open={openBulkDeleteModal} onClose={() => !deleting && setOpenBulkDeleteModal(false)}>
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 480, borderRadius: '8px', outline: 'none', backgroundColor: '#FFFFFF', fontFamily: 'Montserrat !important' }}>
            <Box sx={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Typography sx={{ fontSize: '20px', fontWeight: 600, color: '#1E1E1E', lineHeight: '28px', fontFamily: 'Montserrat !important' }}>
                Delete Holidays
              </Typography>
              <IconButton onClick={() => !deleting && setOpenBulkDeleteModal(false)} sx={{ fontSize: '20px', padding: '0px' }}>
                <IoMdClose />
              </IconButton>
            </Box>
            <hr style={{ margin: '0px 16px' }} />
            <Box sx={{ padding: '16px' }}>
              <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#1E1E1E', fontFamily: 'Montserrat !important' }}>
                There are <strong>{selectedHolidays.length}</strong> record(s) selected for deleting.
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#1E1E1E', mt: 1, fontFamily: 'Montserrat !important' }}>
                Are you sure you want to delete all of them?
              </Typography>
              {deleting && (
                <Typography sx={{ fontSize: '13px', color: '#757575', mt: 1, fontFamily: 'Montserrat !important' }}>
                  Deleting {deleteCount + 1} of {selectedHolidays.length}...
                </Typography>
              )}
            </Box>
            <hr style={{ margin: '0px 16px' }} />
            <Box sx={{ padding: '16px', display: 'flex', gap: '24px' }}>
              <button className="button-secondary w-50" onClick={() => !deleting && setOpenBulkDeleteModal(false)} disabled={deleting}>No</button>
              <button className="button-primary w-50" onClick={onClickConfirmBulkDelete} disabled={deleting}>Yes</button>
            </Box>
          </Box>
        </Modal>

        {/* Upload Modal */}
        <Modal
          open={openUploadModal}
        >
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 660, borderRadius: '8px', outline: 'none', backgroundColor: '#FFFFFF', fontFamily: 'Montserrat !important' }}>
            <Box sx={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography sx={{ fontSize: '20px', fontWeight: 600, color: '#1E1E1E', lineHeight: '28px', fontFamily: 'Montserrat !important' }}>
                  Upload Holiday List
                </Typography>
              </Box>
              <IconButton onClick={() => { setOpenUploadModal(false); setUploadData(null); }} sx={{ fontSize: '20px', padding: '0px' }}>
                <IoMdClose />
              </IconButton>
            </Box>
            <hr style={{ margin: '0px 16px' }} />
            <Box sx={{ padding: '16px' }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#1E1E1E', marginBottom: '8px', fontFamily: 'Montserrat !important' }}>
                Upload files
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    validateAndSetFile(e.target.files[0]);
                  }
                  e.target.value = '';
                }}
                style={{ display: 'none' }}
              />
              {!uploadData && (
                <div
                  className="performer-upload-box p-4 text-center"
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragOver}
                  onDrop={handleDrop}
                >
                  <p className="mb-2" style={{ color: '#757575', fontSize: '14px', fontFamily: 'Montserrat' }}>
                    Drag and drop files here or click to browse
                  </p>
                  <button
                    type="button"
                    className="performer-choose-file-btn mb-2"
                    onClick={(e) => { e.stopPropagation(); fileInputRef.current && fileInputRef.current.click(); }}
                  >
                    Choose File
                  </button>
                  <div>
                    <small style={{ color: '#b2b2b2', fontSize: '12px' }}>
                      Supported formats (Upto 25 Mb): XLS, XLSX
                    </small>
                  </div>
                </div>
              )}
              {uploadData && (
                <div className="performer-upload-successful position-relative">
                  <div className="performer-upload-close position-absolute" onClick={() => setUploadData(null)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M10.0002 5.99992L6.00016 9.99992M6.00016 5.99992L10.0002 9.99992M14.6668 7.99992C14.6668 11.6818 11.6821 14.6666 8.00016 14.6666C4.31826 14.6666 1.3335 11.6818 1.3335 7.99992C1.3335 4.31802 4.31826 1.33325 8.00016 1.33325C11.6821 1.33325 14.6668 4.31802 14.6668 7.99992Z" stroke="#757575" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="p-3 d-flex align-items-center">
                    <p className="m-0" style={{ fontSize: '14px', color: '#1E1E1E', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '90%' }}>
                      {uploadData[0].name}
                    </p>
                  </div>
                </div>
              )}
              <div style={{ marginTop: '12px' }}>
                <a
                  href="/"
                  onClick={downloadSample}
                  style={{ fontSize: '12px', fontWeight: 500, color: '#2965AD', display: 'inline-flex', alignItems: 'center', gap: '6px', textDecoration: 'underline' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M2.66669 11.3333V12.6666C2.66669 13.0202 2.80716 13.3594 3.05721 13.6094C3.30726 13.8595 3.64640 14 4.00002 14H12C12.3536 14 12.6928 13.8595 12.9428 13.6094C13.1929 13.3594 13.3334 13.0202 13.3334 12.6666V11.3333M10.6667 8L8.00002 10.6666M8.00002 10.6666L5.33335 8M8.00002 10.6666V2.66663" stroke="#2965AD" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span style={{ textDecoration: 'underline', textUnderlineOffset: '2px' }}>Download Sample Template</span>
                </a>
              </div>
            </Box>
            <hr style={{ margin: '0px 16px' }} />
            <Box sx={{ padding: '16px', display: 'flex', gap: '24px' }}>
              <button className="button-secondary w-50" onClick={() => { setOpenUploadModal(false); setUploadData(null); }}>
                Cancel
              </button>
              <button className="button-primary w-50" disabled={!uploadData} onClick={onClickSubmitUpload}>
                Upload
              </button>
            </Box>
          </Box>
        </Modal>

        {/* Add / Edit Drawer */}
        {openModal && (
          <div
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1300, display: 'flex', justifyContent: 'flex-end' }}
            onClick={closeFormModal}
          >
            <div
              style={{ width: 700, height: '100%', backgroundColor: '#ffffff', display: 'flex', flexDirection: 'column', fontFamily: 'Montserrat' }}
              onClick={(e) => e.stopPropagation()}
            >
              <Box sx={{ padding: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#1E1E1E', fontFamily: 'Montserrat !important' }}>
                  {modalType === 'View' ? 'View Holiday List' : `${modalType} Holiday`}
                </Typography>
                <IconButton onClick={closeFormModal} sx={{ fontSize: '20px', padding: '0px' }}>
                  <IoMdClose />
                </IconButton>
              </Box>
              <hr style={{ margin: '0' }} />

              <div className="filters sidepanel" style={{ padding: '24px 16px', flex: 1, overflowY: 'auto' }}>
                {modalType === 'View' ? (
                  /* View: two-column label | value rows */
                  <div style={{ fontFamily: 'Montserrat' }}>
                    {[
                      { label: 'Company name', value: (currentHoliday.company && currentHoliday.company.name) || '-' },
                      { label: 'Associate Company Name', value: (currentHoliday.associate_company && currentHoliday.associate_company.name) || '-' },
                      { label: 'Holiday name', value: currentHoliday.name || '-' },
                      { label: 'State', value: (currentHoliday.state && currentHoliday.state.name) || '-' },
                      {
                        label: 'Date',
                        value: currentHoliday.day && currentHoliday.month && currentHoliday.year
                          ? `${String(currentHoliday.day).padStart(2, '0')}-${String(currentHoliday.month).padStart(2, '0')}-${currentHoliday.year}`
                          : '-',
                      },
                      { label: 'Year', value: currentHoliday.year || '-' },
                      { label: 'Restricted', value: currentHoliday.restricted ? 'Yes' : 'No' },
                    ].map(({ label, value }) => (
                      <div
                        key={label}
                        style={{ display: 'flex', alignItems: 'flex-start', padding: '14px 0', }}
                      >
                        <span style={{ flex: '0 0 50%', fontSize: '14px', fontWeight: 600, color: '#1E1E1E' }}>{label}</span>
                        <span style={{ flex: '0 0 50%', fontSize: '14px', fontWeight: 500, color: '#1E1E1E' }}>{value}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  /* Add / Edit: form inputs */
                  <>
                    {/* Name */}
                    <div className="mb-3">
                      <label className="filter-label"><small>Holiday Name <span className="text-danger">*</span></small></label>
                      <input
                        type="text"
                        className="form-control"
                        value={formName}
                        maxLength={30}
                        placeholder="Enter holiday name"
                        style={{ borderRadius: '8px', borderColor: '#D1D5DB', fontSize: '14px' }}
                        onChange={(e) => {
                          if (/^[a-zA-Z0-9\s]*$/.test(e.target.value)) setFormName(e.target.value);
                        }}
                      />
                    </div>

                    {/* Company */}
                    <div className="mb-3">
                      <label className="filter-label"><small>Company <span className="text-danger">*</span></small></label>
                      <Select
                        placeholder="Select Company"
                        options={formCompanyOptions}
                        value={formCompanyOptions.find((o) => o.value === formCompany) || null}
                        onChange={(opt: any) => setFormCompany(opt ? opt.value : '')}
                        className="select-control"
                        components={{ DropdownIndicator: PerformerDropdownIndicator }}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                    </div>

                    {/* Associate Company */}
                    <div className="mb-3">
                      <label className="filter-label"><small>Associate Company <span className="text-danger">*</span></small></label>
                      <Select
                        placeholder="Select Associate Company"
                        options={formAssocCompanyOptions}
                        value={formAssocCompanyOptions.find((o) => o.value === formAssocCompany) || null}
                        onChange={(opt: any) => setFormAssocCompany(opt ? opt.value : '')}
                        isDisabled={!formCompany}
                        className="select-control"
                        components={{ DropdownIndicator: PerformerDropdownIndicator }}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                    </div>

                    <div className="d-flex gap-3">
                    {/* State */}
                      <div className="mb-3 w-50">
                        <label className="filter-label"><small>State <span className="text-danger">*</span></small></label>
                        <Select
                          placeholder="Select State"
                          options={formStateOptions}
                          value={formStateOptions.find((o) => o.value === formState) || null}
                          onChange={(opt: any) => setFormState(opt ? opt.value : '')}
                          isDisabled={!formAssocCompany}
                          className="select-control"
                          components={{ DropdownIndicator: PerformerDropdownIndicator }}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </div>

                      {/* Location */}
                      <div className="mb-3 w-50">
                        <label className="filter-label"><small>Location <span className="text-danger">*</span></small></label>
                        <Select
                          placeholder="Select Location"
                          options={formLocationOptions}
                          value={formLocationOptions.find((o) => o.value === formLocation) || null}
                          onChange={(opt: any) => setFormLocation(opt ? opt.value : '')}
                          isDisabled={!formState}
                          className="select-control"
                          components={{ DropdownIndicator: PerformerDropdownIndicator }}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </div>
                    </div>

                    <div className="d-flex gap-3">
                      {/* Year */}
                      <div className="mb-3 w-100">
                        <label className="filter-label"><small>Year <span className="text-danger">*</span></small></label>
                        <Select
                          placeholder="Select Year"
                          options={YEAR_LIST.map((y) => ({ value: String(y), label: String(y) }))}
                          value={formYear ? { value: formYear, label: formYear } : null}
                          onChange={(opt: any) => { setFormYear(opt ? opt.value : ''); setFormDays([...DAYS_LIST]); }}
                          className="select-control"
                          components={{ DropdownIndicator: PerformerDropdownIndicator }}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </div>

                      {/* Month */}
                      <div className="mb-3 w-100">
                        <label className="filter-label"><small>Month <span className="text-danger">*</span></small></label>
                        <Select
                          placeholder="Select Month"
                          options={MONTH_LIST.map((m) => ({ value: m, label: m }))}
                          value={formMonth ? { value: formMonth, label: formMonth } : null}
                          onChange={(opt: any) => setFormMonth(opt ? opt.value : '')}
                          isDisabled={!formYear}
                          className="select-control"
                          components={{ DropdownIndicator: PerformerDropdownIndicator }}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </div>

                      {/* Day */}
                      <div className="mb-3 w-100">
                        <label className="filter-label"><small>Day <span className="text-danger">*</span></small></label>
                        <Select
                          placeholder="Select Day"
                          options={formDays.map((d) => ({ value: String(d), label: String(d) }))}
                          value={formDay ? { value: formDay, label: formDay } : null}
                          onChange={(opt: any) => setFormDay(opt ? opt.value : '')}
                          isDisabled={!formMonth}
                          className="select-control"
                          components={{ DropdownIndicator: PerformerDropdownIndicator }}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </div>
                    </div>

                    {/* Optional Holiday */}
                    <div className="mb-3">
                      <label className="filter-label d-block"><small>Optional Holiday</small></label>
                      <div className="d-flex p-3">
                        <label className="d-flex align-items-center gap-1 w-50" style={{ cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}>
                          <input type="radio" checked={formOptionalHoliday === true} onChange={() => setFormOptionalHoliday(true)} /> Yes
                        </label>
                        <label className="d-flex align-items-center gap-1 w-50" style={{ cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}>
                          <input type="radio" checked={formOptionalHoliday === false} onChange={() => setFormOptionalHoliday(false)} /> No
                        </label>
                      </div>
                    </div>

                    {/* Restricted */}
                    <div className="mb-3">
                      <label className="filter-label d-block"><small>Restricted</small></label>
                      <div className="d-flex p-3">
                        <label className="d-flex align-items-center gap-1 w-50" style={{ cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}>
                          <input type="radio" checked={formRestricted === true} onChange={() => setFormRestricted(true)} /> Yes
                        </label>
                        <label className="d-flex align-items-center gap-1 w-50" style={{ cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}>
                          <input type="radio" checked={formRestricted === false} onChange={() => setFormRestricted(false)} /> No
                        </label>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div style={{ padding: '16px 20px', display: 'flex', gap: '24px', flexShrink: 0 }}>
                <button className={modalType === 'View' ? 'button-primary w-100' : 'button-secondary w-50'} onClick={closeFormModal}>
                  {modalType === 'View' ? 'Close' : 'Cancel'}
                </button>
                {modalType !== 'View' && (
                  <button
                    className="button-primary w-50"
                    onClick={modalType === 'Add' ? onClickSubmitAdd : onClickSubmitEdit}
                  >
                    Submit
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        <div style={{ height: '100%' }}>
            {/* Page Header */}
            <div className="pageHeading mb-0" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4 className="mb-0">Holiday List</h4>
              {/* Action buttons */}
              <div className="d-flex align-items-end ms-auto" style={{ gap: '10px' }}>
                {hasUserAccess(USER_PRIVILEGES.ADD_PERFORMER_CLIENT_HOLIDAY_LIST) && (
                  <button className="button-primary" onClick={() => setOpenUploadModal(true)}>Upload</button>
                )}
                {hasUserAccess(USER_PRIVILEGES.ADD_PERFORMER_CLIENT_HOLIDAY_LIST) && (
                  <button className="button-primary d-flex align-items-center gap-2" style={{height: "36px"}} onClick={onClickAdd}><span className='fs-4'>+</span> Add</button>
                )}
                {hasUserAccess(USER_PRIVILEGES.DELETE_PERFORMER_CLIENT_HOLIDAY_LIST) && (
                  <button
                    className="button-warning"
                    onClick={onClickBulkDelete}
                    disabled={selectedHolidays.length === 0}
                  >
                    Bulk Delete
                  </button>
                )}
                <button
                  className="download-btn"
                  onClick={onClickExport}
                  disabled={!filterCompany || !filterAssocCompany || !filterState || !filterLocation || holidays.length === 0}
                  title='Export Data'
                >
                  <Icon name="downloadIcon" />
                </button>
              </div>
            </div>

            {/* Filters */}
            <div className="filters" style={{ padding: '16px 20px' }}>
              <div style={{ backgroundColor: '#ffffff', padding: '16px', borderRadius: '8px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '12px' }}>

                  <div className="filter-subcontainer">
                    <label className="filter-label"><small>Company</small></label>
                    <Select
                      options={[{ value: '', label: 'All' }, ...companyOptions]}
                      value={companyOptions.find((o: any) => o.value === filterCompany) || { value: '', label: 'All' }}
                      onChange={(opt: any) => {
                        const val = opt ? opt.value : '';
                        setFilterCompany(val);
                        if (!val) {
                          setFilterAssocCompany('');
                          setFilterState('');
                          setFilterLocation('');
                          setFilterYear('');
                          setFilterMonth('');
                          setPage(0);
                          fetchHolidays({ rawFilters: {}, page: 1 });
                        }
                      }}
                      className="select-control"
                      components={{ DropdownIndicator: PerformerDropdownIndicator }}
                    />
                  </div>

                  <div className="filter-subcontainer">
                    <label className="filter-label"><small>Associate Company</small></label>
                    <Select
                      options={[{ value: '', label: 'All' }, ...assocCompanyOptions]}
                      value={assocCompanyOptions.find((o: any) => o.value === filterAssocCompany) || { value: '', label: 'All' }}
                      onChange={(opt: any) => {
                        const val = opt ? opt.value : '';
                        setFilterAssocCompany(val);
                        if (!val) {
                          setFilterState('');
                          setFilterLocation('');
                          setFilterYear('');
                          setFilterMonth('');
                          setPage(0);
                          fetchHolidays({ rawFilters: { company_id_eq: filterCompany }, page: 1 });
                        }
                      }}
                      isDisabled={!filterCompany}
                      className="select-control"
                      components={{ DropdownIndicator: PerformerDropdownIndicator }}
                    />
                  </div>

                  <div className="filter-subcontainer">
                    <label className="filter-label"><small>State</small></label>
                    <Select
                      options={[{ value: '', label: 'All' }, ...stateOptions]}
                      value={stateOptions.find((o: any) => o.value === filterState) || { value: '', label: 'All' }}
                      onChange={(opt: any) => {
                        const val = opt ? opt.value : '';
                        setFilterState(val);
                        if (!val) {
                          setFilterLocation('');
                          setFilterYear('');
                          setFilterMonth('');
                          setPage(0);
                          fetchHolidays({ rawFilters: { company_id_eq: filterCompany, associate_company_id_eq: filterAssocCompany }, page: 1 });
                        }
                      }}
                      isDisabled={!filterAssocCompany}
                      className="select-control"
                      components={{ DropdownIndicator: PerformerDropdownIndicator }}
                    />
                  </div>

                  <div className="filter-subcontainer">
                    <label className="filter-label"><small>Location</small></label>
                    <Select
                      options={[{ value: '', label: 'All' }, ...locationOptions]}
                      value={locationOptions.find((o: any) => o.value === filterLocation) || { value: '', label: 'All' }}
                      onChange={(opt: any) => {
                        const val = opt ? opt.value : '';
                        setFilterLocation(val);
                        if (!val) {
                          setFilterYear('');
                          setFilterMonth('');
                          setPage(0);
                          fetchHolidays({ rawFilters: { company_id_eq: filterCompany, associate_company_id_eq: filterAssocCompany, state_id_eq: filterState || undefined }, page: 1 });
                        }
                      }}
                      isDisabled={!filterAssocCompany}
                      className="select-control"
                      components={{ DropdownIndicator: PerformerDropdownIndicator }}
                    />
                  </div>

                  <div className="filter-subcontainer">
                    <label className="filter-label"><small>Year</small></label>
                    <Select
                      options={[{ value: '', label: 'All' }, ...YEAR_LIST.map((y) => ({ value: String(y), label: String(y) }))]}
                      value={filterYear ? { value: filterYear, label: filterYear } : { value: '', label: 'All' }}
                      onChange={(opt: any) => {
                        const val = opt ? opt.value : '';
                        setFilterYear(val);
                        if (!val) {
                          setFilterMonth('');
                          setPage(0);
                          fetchHolidays({ rawFilters: { company_id_eq: filterCompany, associate_company_id_eq: filterAssocCompany, state_id_eq: filterState || undefined, location_id_eq: filterLocation ? filterLocation.split('^')[0] : undefined }, page: 1 });
                        }
                      }}
                      className="select-control"
                      components={{ DropdownIndicator: PerformerDropdownIndicator }}
                    />
                  </div>

                  <div className="filter-subcontainer">
                    <label className="filter-label"><small>Month</small></label>
                    <Select
                      options={[{ value: '', label: 'All' }, ...MONTH_LIST.map((m) => ({ value: m, label: m }))]}
                      value={filterMonth ? { value: filterMonth, label: filterMonth } : { value: '', label: 'All' }}
                      onChange={(opt: any) => {
                        const val = opt ? opt.value : '';
                        setFilterMonth(val);
                        if (!val) {
                          setPage(0);
                          fetchHolidays({ rawFilters: { company_id_eq: filterCompany, associate_company_id_eq: filterAssocCompany, state_id_eq: filterState || undefined, location_id_eq: filterLocation ? filterLocation.split('^')[0] : undefined, year_eq: filterYear || undefined }, page: 1 });
                        }
                      }}
                      isDisabled={!filterYear}
                      className="select-control"
                      components={{ DropdownIndicator: PerformerDropdownIndicator }}
                    />
                  </div>

                  
                  <div style={{ position: 'relative', width: '240px', height: '36px', borderRadius: '9999px', border: '1px solid #D9D9D9', backgroundColor: '#ffffff', flexShrink: 0 }}>
                    <input
                      type="text"
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', padding: '8px 60px 8px 14px', border: 'none', outline: 'none', borderRadius: '9999px', fontSize: '14px', fontWeight: 500, backgroundColor: 'transparent', color: '#2C2C2C' }}
                      placeholder="Search (Holiday Name)"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                    />
                    {searchInput && (
                      <button
                      style={{ position: 'absolute', right: '30px', top: 0, width: '30px', height: '36px', background: 'none', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}
                      onClick={() => setSearchInput('')}
                      >
                      <IoMdClose style={{ fontSize: '16px', color: '#757575' }} />
                      </button>
                    )}
                    <button
                      style={{ position: 'absolute', right: 0, top: 0, width: '36px', height: '36px', background: 'none', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}
                      onClick={handleSearch}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M14 14L11.1 11.1M12.6667 7.33333C12.6667 10.2789 10.2789 12.6667 7.33333 12.6667C4.38781 12.6667 2 10.2789 2 7.33333C2 4.38781 4.38781 2 7.33333 2C10.2789 2 12.6667 4.38781 12.6667 7.33333Z" stroke="#2C2C2C" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>

                </div>
              </div>
            </div>

            {/* Table */}
            <div style={{ padding: '0 20px' }} className='holidayList_table'>
              <PerformerTable
                onSelectionChange={(rows) => setSelectedHolidays(rows.map((r: any) => r.id))}
                columns={[
                  { label: 'Year', sortKey: 'year', render: (row) => row.year },
                  { label: 'Date', render: (row) => formatDate(row) },
                  { label: 'Company', sortKey: 'company_name', render: (row) => row.company && row.company.name },
                  { label: 'Associate Company', sortKey: 'associate_company_name', render: (row) => row.associate_company && row.associate_company.name },
                  { label: 'Holiday Name', sortKey: 'name', render: (row) => row.name },
                  { label: 'State', render: (row) => row.state && row.state.name },
                  { label: 'Restricted', sortKey: 'restricted', render: (row) => (row.restricted ? 'Yes' : 'No') },
                  {
                    label: 'Actions',
                    render: (row) => (
                      <div className="d-flex gap-3 align-items-end">
                        {hasUserAccess(USER_PRIVILEGES.EDIT_PERFORMER_CLIENT_HOLIDAY_LIST) && (
                          <img src={editIcon} alt="editIcon" onClick={() => onclickEdit(row)}/>
                        )}
                        {hasUserAccess(USER_PRIVILEGES.DELETE_PERFORMER_CLIENT_HOLIDAY_LIST) && (
                          <Icon
                            action={() => onclickDelete(row)}
                            type="button"
                            name="deleteIcon"
                            text="Delete"
                          />
                        )}
                        {hasUserAccess(USER_PRIVILEGES.VIEW_PERFORMER_CLIENT_HOLIDAY_LIST) && (
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none" onClick={() => onclickView(row)}>
                            <path d="M0.666626 8.65627C0.666626 8.65627 3.33329 3.32294 7.99996 3.32294C12.6666 3.32294 15.3333 8.65627 15.3333 8.65627C15.3333 8.65627 12.6666 13.9896 7.99996 13.9896C3.33329 13.9896 0.666626 8.65627 0.666626 8.65627Z" stroke="#2965AD" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M7.99996 10.6563C9.10453 10.6563 9.99996 9.76084 9.99996 8.65627C9.99996 7.5517 9.10453 6.65627 7.99996 6.65627C6.89539 6.65627 5.99996 7.5517 5.99996 8.65627C5.99996 9.76084 6.89539 10.6563 7.99996 10.6563Z" stroke="#2965AD" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>
                    ),
                  },
                ]}
                data={holidays}
                count={holidayCount}
                page={page}
                rowsPerPage={rowsPerPage}
                activeSort={activeSort}
                sortType={sortType}
                onSort={handleSort}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </div>
          </div>

      </div>
    </div>
    {loading && <PageLoader>Loading...</PageLoader>}
    </>
  );
};

export default HolidayListPerformer;